import { useModuleBEM, IconNameEnum } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import styles from "./CarLocationAutocomplete.module.scss";
import { Autocomplete } from "@/components";
import {
  fetchCarsLocations,
  type CarsLocationSuggestion,
} from "@/utils/fetchCarsLocations";

export interface CarLocationAutocompleteProps {
  id: string;
  label: string;
  placeholder?: string;
  defaultValue?: CarsLocationSuggestion;
  onChange: (value?: CarsLocationSuggestion) => void;
  className?: string;
  returnToSameLocation?: () => void;
}

const optionToIcon = {
  airport: IconNameEnum.airplane,
  place: IconNameEnum.mapPin,
  unknown: IconNameEnum.mapPin,
};

const SAME_LOCATION_IDENTIFIER = "same-location";

const shouldFetch = (search: string) => search.length >= 2;

const getOptionLabel = (value?: CarsLocationSuggestion) => {
  return value?.label ?? "";
};

export const CarLocationAutocomplete = (
  props: CarLocationAutocompleteProps
) => {
  const {
    id,
    label,
    placeholder,
    defaultValue,
    onChange,
    className,
    returnToSameLocation,
  } = props;
  const COMPONENT_KEY = "CarLocationAutocomplete";
  const { t } = useI18nContext();
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const getOptionIcon = (option: CarsLocationSuggestion) => {
    const iconName = option.category
      ? optionToIcon[option.category]
      : IconNameEnum.mapPin;
    return (
      <Icon
        iconName={iconName}
        width="16"
        height="16"
        className={cn("option-icon")}
      />
    );
  };

  const getOptionComponent = (option: CarsLocationSuggestion) => {
    const labelParts = option.label.split(",");
    const labelTitle = labelParts[0].trim();
    const labelSubtitle = labelParts.slice(1).join(", ").trim();
    const icon = getOptionIcon(option);
    return (
      <div className={cn("option-container")}>
        <div className={cn("option-icon-container")}>{icon}</div>
        <div className={cn("option-label-container")}>
          <div className={cn("option-label")}>{labelTitle}</div>
          {labelSubtitle && (
            <div className={cn("option-subtitle")}>{labelSubtitle}</div>
          )}
        </div>
      </div>
    );
  };

  const sameLocationOption: CarsLocationSuggestion[] = returnToSameLocation
    ? [
        {
          label: t("core-cars.search.sameLocationLabel"),
          category: "place" as const,
          identifier: SAME_LOCATION_IDENTIFIER,
          locationType: "Place" as const,
        },
      ]
    : [];

  // Custom onChange that handles the special same location option
  const handleChange = (value?: CarsLocationSuggestion) => {
    if (
      value?.identifier === SAME_LOCATION_IDENTIFIER &&
      returnToSameLocation
    ) {
      returnToSameLocation();
    } else {
      onChange(value);
    }
  };

  return (
    <Autocomplete<CarsLocationSuggestion>
      id={id}
      defaultValue={defaultValue}
      onChange={handleChange}
      fetch={(apiConfig, search, onSuccess) =>
        fetchCarsLocations(search, onSuccess)
      }
      staticOptions={sameLocationOption}
      shouldFetch={shouldFetch}
      getOptionLabel={getOptionLabel}
      label={label}
      placeholder={placeholder}
      className={block}
      icon={<Icon iconName={IconNameEnum.mapPin} className={cn("icon")} />}
      getOptionComponent={getOptionComponent}
      popperClassName={cn("popper")}
    />
  );
};
